/**
 * The curated icon set.
 *
 * There is no icon package in this repo — no font, no @iconify, no tree-shaken
 * 4,000-glyph library of which we would ship six. These are hand-kept geometry
 * definitions on a shared 24×24 grid, stroked in `currentColor` at a uniform
 * weight, so an icon inherits its colour from the text beside it and never
 * needs a colour token of its own.
 *
 * ## The `directional` flag — this is the RTL bit that matters
 *
 * The portal is Thaana-first and runs under `dir="rtl"`. Two kinds of icon
 * live in this file and they behave differently there:
 *
 *   - **Directional** icons encode a direction *in the reading order*: a
 *     chevron that means "next", an arrow that means "back", the export arrow
 *     leaving a box. Under RTL these must mirror, or "next" points backwards.
 *   - **Everything else** encodes a thing: a clock face, a checkmark, a fish, a
 *     magnifier. Mirroring these makes a clock run backwards and a search glass
 *     look broken.
 *
 * Deciding per icon, here, once, is the entire mechanism. `UiIcon` turns the
 * flag into a class and `styles.css` does the flip. No call site ever thinks
 * about it, and there is no mirrored stylesheet.
 */

export interface IconDefinition {
  /** Mirror horizontally under `dir="rtl"`. See the note above. */
  directional?: boolean
  /** Stroked path data on the 24×24 grid. */
  paths?: readonly string[]
  /** `[cx, cy, r]` */
  circles?: readonly (readonly [number, number, number])[]
  /** `[x, y, width, height, rx]` */
  rects?: readonly (readonly [number, number, number, number, number])[]
  /** Paths filled with `currentColor` instead of stroked — dots, solid marks. */
  filled?: readonly string[]
}

export const ICONS = {
  // --- Direction: every one of these mirrors under RTL ----------------------
  'chevron-left': { directional: true, paths: ['m15 5-7 7 7 7'] },
  'chevron-right': { directional: true, paths: ['m9 5 7 7-7 7'] },
  'chevron-up': { paths: ['m5 15 7-7 7 7'] },
  'chevron-down': { paths: ['m5 9 7 7 7-7'] },
  'chevrons-left': { directional: true, paths: ['m11 5-7 7 7 7', 'm19 5-7 7 7 7'] },
  'chevrons-right': { directional: true, paths: ['m5 5 7 7-7 7', 'm13 5 7 7-7 7'] },
  'arrow-left': { directional: true, paths: ['M20 12H4', 'm10 6-6 6 6 6'] },
  'arrow-right': { directional: true, paths: ['M4 12h16', 'm14 6 6 6-6 6'] },
  'arrow-up': { paths: ['M12 20V4', 'm6 10 6-6 6 6'] },
  'arrow-down': { paths: ['M12 4v16', 'm6 14 6 6 6-6'] },
  'arrow-up-right': { directional: true, paths: ['M7 17 17 7', 'M8 7h9v9'] },
  'external-link': {
    directional: true,
    paths: ['M14 4h6v6', 'M20 4 11 13', 'M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5'],
  },
  'log-out': { directional: true, paths: ['M15 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4', 'M10 8l-4 4 4 4', 'M6 12h10'] },
  'corner-down-right': { directional: true, paths: ['M5 5v8a2 2 0 0 0 2 2h10', 'm14 11 4 4-4 4'] },

  // --- Status --------------------------------------------------------------
  check: { paths: ['m4 12.5 5.5 5.5L20 6.5'] },
  'check-circle': { circles: [[12, 12, 9]], paths: ['m8 12 3 3 5-6'] },
  x: { paths: ['M6 6l12 12', 'M18 6 6 18'] },
  'x-circle': { circles: [[12, 12, 9]], paths: ['m9 9 6 6', 'm15 9-6 6'] },
  'alert-triangle': {
    paths: ['M12 4 2.6 20.2h18.8z', 'M12 10v4'],
    filled: ['M12 16.4a.9.9 0 1 1 0 1.8.9.9 0 0 1 0-1.8z'],
  },
  'alert-circle': { circles: [[12, 12, 9]], paths: ['M12 7.5v5'], filled: ['M12 15.6a.9.9 0 1 1 0 1.8.9.9 0 0 1 0-1.8z'] },
  info: { circles: [[12, 12, 9]], paths: ['M12 16.5v-5'], filled: ['M12 6.6a.9.9 0 1 1 0 1.8.9.9 0 0 1 0-1.8z'] },
  'help-circle': {
    circles: [[12, 12, 9]],
    paths: ['M9.6 9.4a2.5 2.5 0 1 1 3.3 2.4c-.9.3-1.4 1-1.4 1.8v.3'],
    filled: ['M11.5 16.6a.9.9 0 1 1 0 1.8.9.9 0 0 1 0-1.8z'],
  },
  shield: { paths: ['M12 3 4.5 6v6c0 4.4 3 7.9 7.5 9 4.5-1.1 7.5-4.6 7.5-9V6z', 'm9 12 2.2 2.2L15.5 10'] },
  lock: { rects: [[4.5, 10.5, 15, 10, 2]], paths: ['M8 10.5V8a4 4 0 0 1 8 0v2.5'] },
  ban: { circles: [[12, 12, 9]], paths: ['m5.6 5.6 12.8 12.8'] },

  // --- Actions -------------------------------------------------------------
  search: { circles: [[11, 11, 7]], paths: ['m16.2 16.2 4.3 4.3'] },
  filter: { paths: ['M3.5 5h17l-6.7 7.8V19l-3.6 2v-8.2z'] },
  download: { paths: ['M12 3.5v11.5', 'm7 10 5 5 5-5', 'M4 20.5h16'] },
  upload: { paths: ['M12 20.5V9', 'm7 14 5-5 5 5', 'M4 3.5h16'] },
  plus: { paths: ['M12 5v14', 'M5 12h14'] },
  minus: { paths: ['M5 12h14'] },
  pencil: { paths: ['M4 20h4.2L20 8.2 15.8 4 4 15.8z', 'm14 6 4 4'] },
  trash: { paths: ['M4 6.5h16', 'M9 6.5V4h6v2.5', 'm6.5 6.5 1 13.5h9l1-13.5', 'M10.5 10.5v6', 'M13.5 10.5v6'] },
  copy: { rects: [[9, 9, 11, 11, 2]], paths: ['M15 5.5H6a1.5 1.5 0 0 0-1.5 1.5v9'] },
  refresh: { paths: ['M20 12a8 8 0 1 1-2.6-5.9', 'M20.5 4v5h-5'] },
  'more-horizontal': { filled: ['M6 10.6a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z', 'M12 10.6a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z', 'M18 10.6a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z'] },
  'more-vertical': { filled: ['M12 4.6a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z', 'M12 10.6a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z', 'M12 16.6a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z'] },
  menu: { paths: ['M4 7h16', 'M4 12h16', 'M4 17h16'] },
  columns: { rects: [[3.5, 4.5, 17, 15, 2]], paths: ['M9.5 4.5v15', 'M15.5 4.5v15'] },
  eye: { paths: ['M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z'], circles: [[12, 12, 3]] },
  'eye-off': { paths: ['M4 4l16 16', 'M9.9 5.9A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3.4 4.1', 'M6.4 7.9A17 17 0 0 0 2.5 12S6 18.5 12 18.5c1.3 0 2.5-.3 3.5-.7', 'M10 10.2a2.6 2.6 0 0 0 3.7 3.6'] },
  print: { paths: ['M7 9V3.5h10V9', 'M7 17H5a1.5 1.5 0 0 1-1.5-1.5v-5A1.5 1.5 0 0 1 5 9h14a1.5 1.5 0 0 1 1.5 1.5v5A1.5 1.5 0 0 1 19 17h-2', 'M7 14h10v6.5H7z'] },

  // --- Objects and domain --------------------------------------------------
  calendar: { rects: [[3.5, 5, 17, 15.5, 2]], paths: ['M3.5 10h17', 'M8 3v4', 'M16 3v4'] },
  clock: { circles: [[12, 12, 9]], paths: ['M12 7v5.2l3.2 1.9'] },
  user: { circles: [[12, 8, 4]], paths: ['M4.5 20.5a7.5 7.5 0 0 1 15 0'] },
  users: { circles: [[9.5, 8, 3.6]], paths: ['M2.5 20.5a7 7 0 0 1 14 0', 'M16.2 4.9a3.6 3.6 0 0 1 0 6.9', 'M17.5 14.4a6.5 6.5 0 0 1 4 6.1'] },
  settings: {
    circles: [[12, 12, 3]],
    paths: [
      'M19.1 14.6a1.6 1.6 0 0 0 .3 1.8l.1.1a1.9 1.9 0 1 1-2.7 2.7l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.2a1.9 1.9 0 0 1-3.8 0v-.1a1.6 1.6 0 0 0-2.7-1.2l-.1.1a1.9 1.9 0 1 1-2.7-2.7l.1-.1a1.6 1.6 0 0 0-1.1-2.7h-.2a1.9 1.9 0 0 1 0-3.8h.1a1.6 1.6 0 0 0 1.2-2.7l-.1-.1a1.9 1.9 0 1 1 2.7-2.7l.1.1a1.6 1.6 0 0 0 2.7-1.1v-.2a1.9 1.9 0 0 1 3.8 0v.1a1.6 1.6 0 0 0 2.7 1.2l.1-.1a1.9 1.9 0 1 1 2.7 2.7l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.2a1.9 1.9 0 0 1 0 3.8h-.1a1.6 1.6 0 0 0-1.5 1z',
    ],
  },
  home: { paths: ['M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1z'] },
  bell: { paths: ['M18 9a6 6 0 0 0-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9z', 'M13.7 19.5a2 2 0 0 1-3.4 0'] },
  mail: { rects: [[3, 5.5, 18, 13, 2]], paths: ['m3.5 7 8.5 6 8.5-6'] },
  phone: { paths: ['M7.5 3.5h-2A2 2 0 0 0 3.5 5.7a17.5 17.5 0 0 0 14.8 14.8 2 2 0 0 0 2.2-2v-2a1.4 1.4 0 0 0-1.2-1.4l-2.6-.4a1.4 1.4 0 0 0-1.4.7l-.8 1.4a13.5 13.5 0 0 1-5.3-5.3l1.4-.8a1.4 1.4 0 0 0 .7-1.4l-.4-2.6a1.4 1.4 0 0 0-1.4-1.2z'] },
  'map-pin': { paths: ['M12 21.5s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11z'], circles: [[12, 10.3, 2.6]] },
  ship: { paths: ['M4 13.5 12 10l8 3.5', 'M12 10V5.5H8', 'M3 17.5c1.6 0 1.6 1.5 3.2 1.5s1.6-1.5 3.2-1.5 1.6 1.5 3.2 1.5 1.6-1.5 3.2-1.5 1.6 1.5 3.2 1.5', 'M5.5 14.4 6.6 18h10.8l1.1-3.6'] },
  // The tail forks AWAY from the body, not into it. The previous drawing put
  // the tail's apex at (3,12) — which was also the body's own leftmost point —
  // and ran its two arms rightward, so the fin opened toward the body and
  // overlapped it. It read as a fish with its tail on backwards.
  // Body now ends at x=7 and the tail occupies x=2..7 outside it.
  fish: {
    paths: [
      'M7 12c1.8-4.2 5.4-6.5 9-6.5 2.8 0 5 1.5 6 6.5-1 5-3.2 6.5-6 6.5-3.6 0-7.2-2.3-9-6.5z',
      'M7.4 12 2.5 8.2v7.6z',
    ],
    filled: ['M17.6 10.1a.9.9 0 1 1 0 1.8.9.9 0 0 1 0-1.8z'],
  },
  scale: { paths: ['M12 4v16', 'M7 20h10', 'M4.5 9h15', 'M4.5 9 2 15a3.2 3.2 0 0 0 5 0z', 'M19.5 9 17 15a3.2 3.2 0 0 0 5 0z'] },
  wallet: { paths: ['M4 7.5A2 2 0 0 1 6 5.5h11a2 2 0 0 1 2 2', 'M4 7.5v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V11a2 2 0 0 0-2-2H6a2 2 0 0 1-2-1.5z'], filled: ['M16.6 13.1a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4z'] },
  receipt: { paths: ['M5.5 3.5h13v17l-2.2-1.5-2.2 1.5-2.1-1.5L9.7 20.5 7.5 19l-2 1.5z', 'M9 8h6', 'M9 12h6'] },
  'file-text': { paths: ['M14 3.5H7a1.5 1.5 0 0 0-1.5 1.5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8z', 'M14 3.5V8h4.5', 'M9 12.5h6', 'M9 16h6'] },
  key: { circles: [[7.5, 15.5, 3.5]], paths: ['m10 13 8.5-8.5', 'm15.5 7.5 2 2', 'm18 5 2 2'] },
  link: { paths: ['M10 13.5a4 4 0 0 0 5.7 0l2.6-2.6a4 4 0 1 0-5.7-5.7L11.4 6.5', 'M14 10.5a4 4 0 0 0-5.7 0l-2.6 2.6a4 4 0 1 0 5.7 5.7l1.1-1.1'] },
  database: { paths: ['M4.5 6c0-1.4 3.4-2.5 7.5-2.5S19.5 4.6 19.5 6s-3.4 2.5-7.5 2.5S4.5 7.4 4.5 6z', 'M4.5 6v12c0 1.4 3.4 2.5 7.5 2.5s7.5-1.1 7.5-2.5V6', 'M4.5 12c0 1.4 3.4 2.5 7.5 2.5s7.5-1.1 7.5-2.5'] },
  'wifi-off': { paths: ['M4 4l16 16', 'M9.5 16.5a3.5 3.5 0 0 1 5 0', 'M6 13.2a8 8 0 0 1 3-1.9', 'M15.2 11.5a8 8 0 0 1 2.8 1.7', 'M2.8 9.6A13 13 0 0 1 8 6.6', 'M12.6 6.1a13 13 0 0 1 8.6 3.5'], filled: ['M12 18.9a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2z'] },
  activity: { paths: ['M3 12h4l3 8 4-16 3 8h4'] },

  // --- Data ----------------------------------------------------------------
  'bar-chart': { paths: ['M4 20V10', 'M10 20V4', 'M16 20v-7', 'M3 20.5h18'] },
  'pie-chart': { paths: ['M12 3.5a8.5 8.5 0 1 0 8.5 8.5H12z', 'M15.5 3.2A8.5 8.5 0 0 1 20.8 8.5H15.5z'] },
  'trending-up': { directional: true, paths: ['m3.5 16.5 5.5-5.5 3.5 3.5 6-6', 'M14 8.5h6.5V15'] },
  'trending-down': { directional: true, paths: ['m3.5 7.5 5.5 5.5 3.5-3.5 6 6', 'M14 15.5h6.5V9'] },
  'sort-asc': { paths: ['M6 18V6', 'm2.5 9.5 3.5-3.5 3.5 3.5', 'M12.5 8h9', 'M12.5 13h6', 'M12.5 18h3'] },
  'sort-desc': { paths: ['M6 6v12', 'm2.5 14.5 3.5 3.5 3.5-3.5', 'M12.5 8h9', 'M12.5 13h6', 'M12.5 18h3'] },
  'arrows-sort': { paths: ['M7 4v16', 'm4 7 3-3 3 3', 'M17 20V4', 'm14 17 3 3 3-3'] },

  // --- Theme and locale ----------------------------------------------------
  sun: { circles: [[12, 12, 4]], paths: ['M12 2.5v2', 'M12 19.5v2', 'M4.2 4.2l1.4 1.4', 'M18.4 18.4l1.4 1.4', 'M2.5 12h2', 'M19.5 12h2', 'M4.2 19.8l1.4-1.4', 'M18.4 5.6l1.4-1.4'] },
  moon: { paths: ['M20.5 14.2A8.7 8.7 0 0 1 9.8 3.5a8.7 8.7 0 1 0 10.7 10.7z'] },
  monitor: { rects: [[3, 4.5, 18, 12.5, 2]], paths: ['M8.5 20.5h7', 'M12 17v3.5'] },
  globe: { circles: [[12, 12, 9]], paths: ['M3.2 9.5h17.6', 'M3.2 14.5h17.6', 'M12 3a14 14 0 0 1 0 18', 'M12 3a14 14 0 0 0 0 18'] },
  languages: { paths: ['M3.5 6h8', 'M7.5 4v2', 'M9.7 6c-.4 3.6-2.6 6.6-6.2 8.3', 'M5.5 10.4c1 2 2.7 3.6 5 4.6', 'm12.5 20 4-9 4 9', 'M13.9 17.2h5.2'] },

  // --- Feedback ------------------------------------------------------------
  loader: { paths: ['M12 3v3.5', 'M12 17.5V21', 'M4.9 4.9l2.5 2.5', 'M16.6 16.6l2.5 2.5', 'M3 12h3.5', 'M17.5 12H21', 'M4.9 19.1l2.5-2.5', 'M16.6 7.4l2.5-2.5'] },
  inbox: { paths: ['M3.5 13.5h4l1.5 3h6l1.5-3h4', 'M6.2 4.5h11.6l2.7 9v5a2 2 0 0 1-2 2H5.5a2 2 0 0 1-2-2v-5z'] },

  // --- Assistant -----------------------------------------------------------
  // A four-pointed sparkle with a smaller companion: the mark that now reads
  // as "AI" across the whole software landscape, and the one AI glyph that is
  // NOT a speech bubble — this launcher sits three icons away from a bell and
  // must not be mistaken for notifications or for a comment thread.
  //
  // Not `directional`. A sparkle encodes a thing, not a direction in the
  // reading order; the small companion sits up-and-right of the large one as a
  // composition, and mirroring a composition is exactly the mistake the flag
  // exists to prevent (the same reason a clock face and a magnifier do not
  // mirror).
  //
  // Geometry: each mark is a closed cubic with tips at ±R on the axes and
  // control points at ±d on the perpendicular axis, so d/R sets the waist.
  // The large mark uses d/R = 0.44 (crisp pinch, it has the room); the small
  // one uses 0.50, because a deeper pinch closes its counter at 16px.
  sparkles: {
    paths: [
      'M10.5 5C10.5 9.5 14 13 18.5 13C14 13 10.5 16.5 10.5 21C10.5 16.5 7 13 2.5 13C7 13 10.5 9.5 10.5 5Z',
      'M18.5 2.5C18.5 4.25 20.25 6 22 6C20.25 6 18.5 7.75 18.5 9.5C18.5 7.75 16.75 6 15 6C16.75 6 18.5 4.25 18.5 2.5Z',
    ],
  },
} as const satisfies Record<string, IconDefinition>

export type UiIconName = keyof typeof ICONS

/** Every icon name, for the gallery and for tests that assert coverage. */
export const ICON_NAMES = Object.keys(ICONS) as UiIconName[]
